import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Mail, RefreshCw, Loader2, CheckCircle2, AlertCircle, XCircle,
  Clock, MousePointer, Eye, Inbox, ChevronRight, Send, FileText,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_META = {
  sent:           { label: 'Sent',       icon: Send,         cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  delivered:      { label: 'Delivered',  icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  opened:         { label: 'Opened',     icon: Eye,          cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  clicked:        { label: 'Clicked',    icon: MousePointer, cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  bounced:        { label: 'Bounced',    icon: XCircle,      cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  failed:         { label: 'Failed',     icon: AlertCircle,  cls: 'bg-red-100 text-red-700 border-red-200' },
  deferred:       { label: 'Deferred',   icon: Clock,        cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  dropped:        { label: 'Dropped',    icon: XCircle,      cls: 'bg-gray-200 text-gray-700 border-gray-300' },
  queued_resend:  { label: 'Resend',     icon: RefreshCw,    cls: 'bg-purple-100 text-purple-700 border-purple-200' },
};

// Iter-227b — Email type buckets. Categorize SendGrid `category` strings
// into high-level admin-facing groups so search by type is meaningful.
const EMAIL_TYPE_RULES = [
  { code: 'booking',       label: 'Booking',       match: /booking|invite|invitation|offer|shift|assignment|confirm/i },
  { code: 'reminder',      label: 'Reminder',      match: /reminder|nudge|engagement|follow_up|followup/i },
  { code: 'certification', label: 'Certification', match: /cert|training|quiz|module|exam/i },
  { code: 'review',        label: 'Review',        match: /review|rating|feedback|recap/i },
  { code: 'payment',       label: 'Payment',       match: /payment|payout|invoice|billing|stripe|transfer|deposit/i },
  { code: 'onboarding',    label: 'Onboarding',    match: /onboard|welcome|approval|signup|register|idv|identity|password|reset|agreement/i },
];

function emailTypeFor(category) {
  if (!category) return null;
  for (const r of EMAIL_TYPE_RULES) if (r.match.test(category)) return r.code;
  return null;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || '—', icon: Mail, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  const Icon = m.icon;
  return (
    <span
      data-testid={`comm-status-${status || 'unknown'}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-semibold border ${m.cls}`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * Iter-226e — Communication / Email Log
 *
 * Reusable admin tab for talent / client / agency / coworker profiles.
 * Pulls every outbound email tied to the user's email address from
 * `email_events` (SendGrid event stream) and surfaces sender, subject,
 * category, status, timestamp, status code, error message, and a
 * Resend action.
 *
 * Props:
 *   userId    — the user UUID (preferred). Falls back to email lookup if absent.
 *   email     — the user's email (required when userId not supplied).
 *   testIdScope — string prefix for data-testid hooks (default 'comm-log')
 */
export default function CommunicationLogTab({ userId, email, testIdScope = 'comm-log' }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ count: 0, entries: [], summary: { by_status: {}, by_category: {} } });
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [resending, setResending] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const [bodyEntry, setBodyEntry] = useState(null);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyView, setBodyView] = useState('html');

  const fetchLog = async (silent = false) => {
    if (!userId && !email) return;
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const path = userId
        ? `/admin/comm-log/user/${userId}`
        : `/admin/comm-log/email/${encodeURIComponent(email)}`;
      const r = await api.get(path, { params: { limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load communication log');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };

  useEffect(() => { fetchLog(); /* eslint-disable-next-line */ }, [userId, email]);

  const handleResend = async (entryId) => {
    setResending(entryId);
    try {
      await api.post(`/admin/comm-log/resend/${entryId}`);
      toast.success('Resend queued');
      fetchLog(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setResending(null); }
  };

  // Iter-227c — open the rendered email body in a modal.
  const viewBody = async (entryId) => {
    setBodyLoading(true);
    setBodyEntry({ id: entryId });
    try {
      const r = await api.get(`/admin/comm-log/event/${entryId}`);
      setBodyEntry(r.data);
      setBodyView(r.data.body_html ? 'html' : 'text');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email body');
      setBodyEntry(null);
    } finally { setBodyLoading(false); }
  };

  const filtered = data.entries.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (typeFilter !== 'all' && emailTypeFor(e.category) !== typeFilter) return false;
    if (dateFrom) {
      if (!e.sent_at || new Date(e.sent_at) < new Date(`${dateFrom}T00:00:00`)) return false;
    }
    if (dateTo) {
      if (!e.sent_at || new Date(e.sent_at) > new Date(`${dateTo}T23:59:59`)) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      if (!((e.subject || '').toLowerCase().includes(q) ||
            (e.category || '').toLowerCase().includes(q) ||
            (e.related_id || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Email-type counts for the type-filter pills.
  const typeCounts = data.entries.reduce((acc, e) => {
    const code = emailTypeFor(e.category);
    if (code) acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  const statusChips = Object.entries(data.summary.by_status || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <Card data-testid={`${testIdScope}-card`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Inbox className="w-4 h-4 text-cyan-600" /> Communication Log
            </h3>
            <p className="text-xs text-gray-500" data-testid={`${testIdScope}-summary`}>
              {data.summary?.total ?? 0} total messages
              {data.summary?.last_sent_at && (
                <> · last sent {formatDateTime(data.summary.last_sent_at)}</>
              )}
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => fetchLog(true)}
            disabled={refreshing}
            data-testid={`${testIdScope}-refresh`}
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>

        {/* Status filter chips */}
        {statusChips.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3" data-testid={`${testIdScope}-status-filter`}>
            <button
              type="button"
              onClick={() => setStatusFilter('all')}
              data-testid={`${testIdScope}-filter-all`}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${statusFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
            >
              All ({data.summary?.total ?? 0})
            </button>
            {statusChips.map(([st, cnt]) => {
              const m = STATUS_META[st] || { label: st, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
              const active = statusFilter === st;
              return (
                <button
                  key={st}
                  type="button"
                  onClick={() => setStatusFilter(st)}
                  data-testid={`${testIdScope}-filter-${st}`}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${active ? `${m.cls.replace('border-', 'ring-2 ring-')} border-transparent` : m.cls + ' opacity-70 hover:opacity-100'}`}
                >
                  {m.label} ({cnt})
                </button>
              );
            })}
          </div>
        )}

        {/* Search */}
        <Input
          placeholder="Search subject, category, or event ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-xs mb-2"
          data-testid={`${testIdScope}-search`}
        />

        {/* Email-type buckets (Booking, Reminder, Cert, Review, Payment, Onboarding) */}
        <div className="flex flex-wrap gap-1 mb-2" data-testid={`${testIdScope}-type-filter`}>
          <button
            type="button"
            onClick={() => setTypeFilter('all')}
            data-testid={`${testIdScope}-type-all`}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${typeFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
          >
            All types
          </button>
          {EMAIL_TYPE_RULES.map(rule => {
            const cnt = typeCounts[rule.code] || 0;
            const active = typeFilter === rule.code;
            return (
              <button
                key={rule.code}
                type="button"
                onClick={() => setTypeFilter(rule.code)}
                data-testid={`${testIdScope}-type-${rule.code}`}
                className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${active ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100'}`}
              >
                {rule.label} ({cnt})
              </button>
            );
          })}
        </div>

        {/* Date range */}
        <div className="flex flex-wrap items-end gap-2 mb-3" data-testid={`${testIdScope}-date-range`}>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase text-gray-500 tracking-wider">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid={`${testIdScope}-date-from`}
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase text-gray-500 tracking-wider">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid={`${testIdScope}-date-to`}
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="h-8 text-[11px]"
              data-testid={`${testIdScope}-date-clear`}
            >
              Clear dates
            </Button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-8" data-testid={`${testIdScope}-loading`}>
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-8" data-testid={`${testIdScope}-empty`}>
            No matching messages.
          </p>
        ) : (
          <div className="space-y-1 max-h-[28rem] overflow-y-auto" data-testid={`${testIdScope}-list`}>
            {filtered.map(e => (
              <div
                key={e.id}
                data-testid={`${testIdScope}-entry-${e.id}`}
                className="border border-gray-100 rounded-md p-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setOpenEntry(openEntry === e.id ? null : e.id)}
              >
                <div className="flex items-start gap-2">
                  <ChevronRight className={`w-3.5 h-3.5 text-gray-400 mt-0.5 transition-transform ${openEntry === e.id ? 'rotate-90' : ''}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold text-gray-900 truncate">{e.subject || '(no subject)'}</p>
                      <StatusBadge status={e.status} />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100 text-gray-600 border-0">{e.category || 'general'}</Badge>
                      <span>{formatDateTime(e.sent_at)}</span>
                      {e.to_email && <span>· to {e.to_email}</span>}
                    </div>
                  </div>
                </div>
                {openEntry === e.id && (
                  <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-gray-700" data-testid={`${testIdScope}-detail-${e.id}`}>
                    <div><span className="text-gray-500">Status code:</span> {e.status_code ?? '—'}</div>
                    <div><span className="text-gray-500">Message ID:</span> <code className="text-[10px] break-all">{e.external_id || '—'}</code></div>
                    {e.error_message && (
                      <div className="col-span-2 text-rose-700"><span className="text-gray-500">Error:</span> {e.error_message}</div>
                    )}
                    {e.related_type && e.related_id && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Related:</span>{' '}
                        <span className="font-mono text-[10px]">{e.related_type}/{e.related_id}</span>
                      </div>
                    )}
                    <div className="col-span-2 flex items-center justify-end gap-1 pt-1">
                      {e.has_body && (
                        <Button
                          variant="outline" size="sm"
                          onClick={(ev) => { ev.stopPropagation(); viewBody(e.id); }}
                          data-testid={`${testIdScope}-view-body-${e.id}`}
                          className="h-7 text-[11px]"
                        >
                          <FileText className="w-3 h-3 mr-1" />View email
                        </Button>
                      )}
                      <Button
                        variant="outline" size="sm"
                        onClick={(ev) => { ev.stopPropagation(); handleResend(e.id); }}
                        disabled={resending === e.id}
                        data-testid={`${testIdScope}-resend-${e.id}`}
                        className="h-7 text-[11px]"
                      >
                        {resending === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Resend</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Iter-227c — Email body viewer dialog */}
      <Dialog open={!!bodyEntry} onOpenChange={(o) => { if (!o) setBodyEntry(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid={`${testIdScope}-body-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-cyan-600" />
              {bodyEntry?.subject || 'Email content'}
            </DialogTitle>
          </DialogHeader>
          {bodyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : !bodyEntry ? null : (
            <>
              <div className="text-xs text-gray-600 grid grid-cols-1 sm:grid-cols-2 gap-1 mb-2 pb-2 border-b border-gray-100">
                <div><span className="text-gray-400">To:</span> {bodyEntry.to_email}</div>
                <div><span className="text-gray-400">Sent:</span> {formatDateTime(bodyEntry.sent_at)}</div>
                <div><span className="text-gray-400">Category:</span> {bodyEntry.category || 'general'}</div>
                <div><span className="text-gray-400">Status:</span> {bodyEntry.status}{bodyEntry.status_code ? ` (${bodyEntry.status_code})` : ''}</div>
                {bodyEntry.external_id && (
                  <div className="sm:col-span-2"><span className="text-gray-400">Message ID:</span> <code className="text-[10px]">{bodyEntry.external_id}</code></div>
                )}
              </div>
              {bodyEntry.has_body ? (
                <>
                  <div className="flex gap-1 mb-2">
                    <button
                      onClick={() => setBodyView('html')}
                      disabled={!bodyEntry.body_html}
                      data-testid={`${testIdScope}-body-tab-html`}
                      className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${bodyView === 'html' ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 disabled:opacity-50'}`}
                    >HTML preview</button>
                    <button
                      onClick={() => setBodyView('text')}
                      disabled={!bodyEntry.body_text && !bodyEntry.body_html}
                      data-testid={`${testIdScope}-body-tab-text`}
                      className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold border ${bodyView === 'text' ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 disabled:opacity-50'}`}
                    >Plain text / source</button>
                  </div>
                  <div className="flex-1 overflow-auto border border-gray-200 rounded-md bg-white">
                    {bodyView === 'html' && bodyEntry.body_html ? (
                      <iframe
                        title="email-body"
                        sandbox=""
                        srcDoc={bodyEntry.body_html}
                        className="w-full h-[55vh]"
                        data-testid={`${testIdScope}-body-iframe`}
                      />
                    ) : (
                      <pre className="text-[11px] whitespace-pre-wrap break-words p-3 font-mono text-gray-800" data-testid={`${testIdScope}-body-source`}>
                        {bodyEntry.body_text || bodyEntry.body_html || '(no body stored)'}
                      </pre>
                    )}
                  </div>
                </>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">
                  This message predates body capture (V184 / Iter-227c).<br />
                  Future emails will show their full rendered content here.
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
